/* eslint-disable jsx-a11y/no-noninteractive-element-interactions */
/* eslint-disable jsx-a11y/click-events-have-key-events */
import React, { useState, useEffect, useCallback } from 'react';
import {
  SaveButton,
  FormWithRedirect,
  useCreate,
  useUpdate,
  useNotify,
  useRedirect,
  FormDataConsumer,
} from 'react-admin';
import ExpandLessIcon from '@material-ui/icons/ExpandLess';
import ExpandMoreIcon from '@material-ui/icons/ExpandMore';
import { makeStyles, Grid, Box, Collapse, Typography } from '@material-ui/core';
import dataProvider from '../../../dataProvider';
import ModalView from '../../customer/ModalView';
import BasicsForm from './BasicsForm';
import DisplayForm from './DisplayForm';
import AdvancedServiceLogicForm from './AdvancedServiceLogicForm';
import CustomMessagesForm from './CustomMessagesForm';
import ChecklistForm from './ChecklistForm';
import { isEmpty, shouldAllowMultipleScheduleTypes } from '../../../helper';
import { apiGet } from '../../../brainApi';

const useStyles = makeStyles(() => ({
  clickable: {
    cursor: 'pointer',
  },
  hideShowIcon: {
    fontSize: '18px',
  },
  mTB10: {
    margin: '10px 0',
  },
  m20: {
    margin: '20px',
  },
  center: {
    textAlign: 'center',
  },
  enlargedImage: {
    width: '100%',
    height: 'auto',
  },
}));

const scheduleTypesWithCardView = new Set(['tires', 'detailing']);

const CatalogServiceForm = (props) => {
  const { record } = props;
  const classes = useStyles();
  const notify = useNotify();
  const redirect = useRedirect();
  const [create, { loading: creating }] = useCreate('catalog-service');
  const [update, { loading: updating }] = useUpdate('catalog-service');

  const [isShowingAdvanced, setIsShowingAdvanced] = useState(false);
  const [scheduleTypes, setScheduleTypes] = useState([]);
  const [scheduleTypesAreLoading, setScheduleTypesAreLoading] = useState(false);
  const [priceSheets, setPriceSheets] = useState([]);
  const [priceSheetsAreLoading, setPriceSheetsAreLoading] = useState(false);
  const [prices, setPrices] = useState([]);
  const [pricesAreLoading, setPricesAreLoading] = useState(false);
  const [customMessagesList, setCustomMessagesList] = useState([
    { id: 'new', internal_name: '＋ Create new' },
  ]);
  const [newCustomMessages, setNewCustomMessages] = useState({});
  const [editedCustomMessages, setEditedCustomMessages] = useState({});
  const [selectedMessage1, setSelectedMessage1] = useState(null);
  const [selectedMessage2, setSelectedMessage2] = useState(null);
  const [isImageModalOpen, setIsImageModalOpen] = useState(false);
  const [imageUrl, setImageUrl] = useState('');
  const [isBookable, setIsBookable] = useState(false);
  const [selectedScheduleTypeIds, setSelectedScheduleTypeIds] = useState([]);
  const [addOilCostBool, setAddOilCostBool] = useState(false);
  const [extraOilCost, setExtraOilCost] = useState(0);
  const [singlePartsCost, setSinglePartsCost] = useState(0);
  const [singleLaborCost, setSingleLaborCost] = useState(0);
  const [isCardView, setIsCardView] = useState(false);
  const [imageIsSaving, setImageIsSaving] = useState(false);
  const [displayCategory, setDisplayCategory] = useState('standard');
  const [errorMessage, setErrorMessage] = useState(null);
  const [displayRef, setDisplayRef] = useState(null);
  const [defaultDisplayOrder, setDefaultDisplayOrder] = useState(1);
  const allowMultipleScheduleTypes = shouldAllowMultipleScheduleTypes(props);

  const didUserSelectCardView = () => {
    if (selectedScheduleTypeIds.length < 1) {
      return false;
    }
    const selectedScheduleTypeNames = scheduleTypes
      .filter((schType) => selectedScheduleTypeIds.includes(schType.id))
      .map(({ name }) => name);
    return selectedScheduleTypeNames.some((name) =>
      scheduleTypesWithCardView.has(name),
    );
  };

  const isSingleScheduleTypeCardView = () => {
    if (scheduleTypesAreLoading || scheduleTypes.length !== 1) {
      return false;
    }
    return scheduleTypesWithCardView.has(scheduleTypes[0].name);
  };

  // Set isCardView based on whether:
  // 1. There are multiple schedule types and user selects one
  // 2. Only one schedule type exists
  useEffect(() => {
    const userSelectedCardView = didUserSelectCardView();
    const singleScheduleTypeCardView = isSingleScheduleTypeCardView();
    setIsCardView(userSelectedCardView || singleScheduleTypeCardView);
  }, [scheduleTypesAreLoading, scheduleTypes, selectedScheduleTypeIds]);

  const getPriceSheets = useCallback(() => {
    setPriceSheetsAreLoading(true);
    dataProvider
      .getList('price-sheet', {
        filter: {},
        sort: { field: 'price_sheet_id', order: 'ASC' },
        pagination: { page: 1, perPage: 100 },
      })
      .then((response) => {
        setPriceSheetsAreLoading(false);
        if (response.data) {
          setPriceSheets(response.data);
        }
      })
      .catch((error) => {
        setPriceSheetsAreLoading(false);
        notify('Error retrieving price sheets', 'warning');
        console.error(error);
      });
  }, []);

  const getPrices = useCallback(() => {
    setPricesAreLoading(true);
    dataProvider
      .getList('price', {
        filter: {
          service_offering_id: record.service_offering_id || null,
        },
        sort: { field: 'price_sheet_name', order: 'ASC' },
        pagination: { page: 1, perPage: 100 },
      })
      .then((response) => {
        const data = response?.data;
        if (data) {
          // If there is a default row, the prices were created with overrides -- set is_checked for the default row and any rows that differ to true
          const defaultPrice = data.find(
            (el) => el.price_sheet_name === 'Default',
          );
          let dataWithIsChecked;
          if (defaultPrice) {
            dataWithIsChecked = data.map((price) => {
              const isChecked =
                price.price_sheet_name === 'Default' ||
                price.parts_cost_dollars !== defaultPrice.parts_cost_dollars ||
                price.labor_cost_dollars !== defaultPrice.labor_cost_dollars;
              return { ...price, is_checked: isChecked };
            });
          } else {
            // If there is no default row, prices were created selectively -- set is_checked for all existing prices to true
            dataWithIsChecked = data.map((price) => ({
              ...price,
              is_checked: true,
            }));
          }
          setPrices(dataWithIsChecked);

          if (data.length) {
            setExtraOilCost(data[0].extra_oil_cost_per_qt_dollars);
            setSinglePartsCost(data[0].parts_cost_dollars);
            setSingleLaborCost(data[0].labor_cost_dollars);
          }
        }
      })
      .then(() => {
        setPricesAreLoading(false);
      })
      .catch((error) => {
        setPricesAreLoading(false);
        notify('Error retrieving prices', 'warning');
        console.error(error);
      });
  }, []);

  const getScheduleTypes = useCallback(() => {
    setScheduleTypesAreLoading(true);
    dataProvider
      .getList('schedule-type', {
        filter: {},
        sort: { field: 'schedule_type_id', order: 'ASC' },
        pagination: { page: 1, perPage: 500 },
      })
      .then((response) => {
        setScheduleTypesAreLoading(false);
        setScheduleTypes(response.data);
      })
      .catch((error) => {
        setScheduleTypesAreLoading(false);
        notify('Error retrieving schedule types', 'warning');
        console.error(error);
      });
  }, []);

  const getHighestDisplayOrder = () => {
    apiGet('/service-offering').then((services) => {
      if (services.length > 0) {
        const displayOrders = services
          .map((s) => s.display_order)
          .sort((a, b) => a - b);
        // By default, set the display order for the new service to be 1 more than the current max
        setDefaultDisplayOrder(displayOrders[displayOrders.length - 1] + 1);
      }
    });
  };

  useEffect(() => {
    getPriceSheets();
    getScheduleTypes();
    getPrices();
    getHighestDisplayOrder();
  }, []);

  // If editing a record, set initial values
  useEffect(() => {
    if (record.custom_message_id_1) {
      setSelectedMessage1(record.custom_message_1);
    }
    if (record.custom_message_id_2) {
      setSelectedMessage2(record.custom_message_2);
    }
    if (record.schedule_type_ids) {
      setSelectedScheduleTypeIds(record.schedule_type_ids);
    }
    if (record.display_category) {
      setDisplayCategory(record.display_category);
    }
  }, [record]);

  const handleImageClick = (url) => {
    setIsImageModalOpen(true);
    setImageUrl(url);
  };

  const getDisplayRef = (ref) => {
    setDisplayRef(ref);
  };

  const handleSubmit = (values) => {
    setErrorMessage(null);

    // Validate custom message internal name
    if (
      Object.values(newCustomMessages).some(
        (m) => !m.internal_name || m.internal_name.length < 2,
      ) ||
      Object.values(editedCustomMessages).some(
        (m) => !m.internal_name || m.internal_name.length < 2,
      )
    ) {
      setErrorMessage(
        'Service-specific messages must have a valid message name',
      );
      return;
    }

    // Validate prices
    if (
      (isBookable &&
        prices.length &&
        prices.every((p) => p.is_checked === false)) ||
      (!singlePartsCost && singlePartsCost !== 0) ||
      (!singleLaborCost && singleLaborCost !== 0)
    ) {
      setErrorMessage(
        'Must add price: Services with no prices will not appear on the booking site',
      );
      return;
    }

    const formValues = values;

    // If long name/tiny internal name is blank or hasn't been changed, use short name
    if (
      !formValues.long_name ||
      (formValues.long_name === record.long_name &&
        formValues.long_name === record.short_name)
    ) {
      formValues.long_name = formValues.short_name;
    }
    if (
      !formValues.tiny_internal_name ||
      (formValues.tiny_internal_name === record.tiny_internal_name &&
        formValues.tiny_internal_name === record.short_name)
    ) {
      formValues.tiny_internal_name = formValues.short_name;
    }

    // If there is a single schedule type available, user does not have the option to select schedule type. Automatically set it to the single price sheet
    if (scheduleTypes.length === 1) {
      formValues.schedule_type_ids = [scheduleTypes[0].id];
    } else {
      formValues.schedule_type_ids = selectedScheduleTypeIds;
    }

    // Send prices from EditablePriceGrid or for single price sheet
    if (priceSheets.length > 1) {
      delete formValues.parts_cost_dollars;
      delete formValues.labor_cost_dollars;
      formValues.prices = prices;
    } else if (priceSheets.length === 1) {
      formValues.prices = [
        {
          price_sheet_id: priceSheets[0].id,
          parts_cost_dollars: singlePartsCost,
          labor_cost_dollars: singleLaborCost,
          is_checked: true,
        },
      ];
    }

    // Add extra_oil_cost_per_qt_dollars depending on addOilCostBool toggle
    if (addOilCostBool) {
      formValues.extra_oil_cost_per_qt_dollars = extraOilCost;
    } else {
      formValues.extra_oil_cost_per_qt_dollars = null;
    }

    // Add display_category value since it isn't rendered in Basics
    formValues.display_category = displayCategory;

    // The form's custom_message_id_1 and custom_message_id_2 are for existing, unedited messages. They don't reflect new/edited messages.
    if (
      newCustomMessages[1]?.internal_name ||
      editedCustomMessages[1]?.internal_name
    ) {
      delete formValues.custom_message_id_1;
    }
    if (
      newCustomMessages[2]?.internal_name ||
      editedCustomMessages[2]?.internal_name
    ) {
      delete formValues.custom_message_id_2;
    }
    if (!selectedMessage1?.id) formValues.custom_message_id_1 = null;
    if (!selectedMessage2?.id) formValues.custom_message_id_2 = null;

    formValues.new_custom_messages = newCustomMessages;
    formValues.edited_custom_messages = editedCustomMessages;

    if (isEmpty(record)) {
      create(
        { payload: { data: { ...formValues } } },
        {
          onSuccess: () => {
            notify('Catalog service created successfully');
            redirect('/service-offering');
          },
          onFailure: ({ error }) => {
            notify(
              error?.message || 'An error occurred while creating this service',
              'error',
            );
          },
        },
      );
    } else {
      update(
        {
          payload: {
            id: record.id,
            data: { ...formValues },
            previousData: record,
          },
        },
        {
          onSuccess: () => {
            notify('Catalog service updated successfully');
            redirect('/service-offering');
          },
          onFailure: ({ error }) => {
            notify(
              error?.message || 'An error occurred while updating this service',
              'error',
            );
          },
        },
      );
    }
  };

  if (scheduleTypesAreLoading || priceSheetsAreLoading || pricesAreLoading) {
    return 'Loading...';
  }

  return (
    <FormWithRedirect
      {...props}
      save={handleSubmit}
      render={({ handleSubmitWithRedirect, invalid }) => (
        <Box className="unifiedWrapper">
          <Grid container justify="center">
            <Grid item xs={12} sm={8}>
              <Box pl={1.5} pr={1} pt={2}>
                {/* Keep some form values in state to use as conditions */}
                <FormDataConsumer>
                  {({ formData: { bookable, add_oil_cost } }) => {
                    if (bookable !== isBookable) setIsBookable(bookable);
                    if (add_oil_cost !== addOilCostBool)
                      setAddOilCostBool(add_oil_cost);
                  }}
                </FormDataConsumer>
                <BasicsForm
                  {...props}
                  priceSheets={priceSheets}
                  prices={prices}
                  setPrices={setPrices}
                  scheduleTypes={scheduleTypes}
                  setSelectedScheduleTypeIds={setSelectedScheduleTypeIds}
                  singlePartsCost={singlePartsCost}
                  setSinglePartsCost={setSinglePartsCost}
                  singleLaborCost={singleLaborCost}
                  setSingleLaborCost={setSingleLaborCost}
                  displayRef={displayRef}
                  isShowingAdvanced={isShowingAdvanced}
                  setIsShowingAdvanced={setIsShowingAdvanced}
                  isBookable={isBookable}
                  allowMultipleScheduleTypes={allowMultipleScheduleTypes}
                  errorMessage={errorMessage}
                />
                <Box
                  onClick={() => setIsShowingAdvanced(!isShowingAdvanced)}
                  className={`${classes.clickable} ${classes.mTB10} ${classes.center}`}
                >
                  {isShowingAdvanced ? (
                    <ExpandLessIcon className={classes.hideShowIcon} />
                  ) : (
                    <ExpandMoreIcon className={classes.hideShowIcon} />
                  )}
                  {isShowingAdvanced ? 'Hide' : 'Show'} Advanced Fields
                </Box>
                <Collapse in={isShowingAdvanced}>
                  {isBookable && (
                    <DisplayForm
                      {...props}
                      handleImageClick={handleImageClick}
                      scheduleTypesWithCardView={scheduleTypesWithCardView}
                      selectedScheduleTypeIds={selectedScheduleTypeIds}
                      isCardView={isCardView}
                      setImageIsSaving={setImageIsSaving}
                      scheduleTypes={scheduleTypes}
                      getDisplayRef={getDisplayRef}
                      defaultDisplayOrder={defaultDisplayOrder}
                    />
                  )}
                  <AdvancedServiceLogicForm
                    {...props}
                    addOilCostBool={addOilCostBool}
                    extraOilCost={extraOilCost}
                    setExtraOilCost={setExtraOilCost}
                    setDisplayCategory={setDisplayCategory}
                  />
                  <CustomMessagesForm
                    {...props}
                    resource="service-custom-message"
                    customMessagesList={customMessagesList}
                    setCustomMessagesList={setCustomMessagesList}
                    newCustomMessages={newCustomMessages}
                    setNewCustomMessages={setNewCustomMessages}
                    editedCustomMessages={editedCustomMessages}
                    setEditedCustomMessages={setEditedCustomMessages}
                    selectedMessage1={selectedMessage1}
                    setSelectedMessage1={setSelectedMessage1}
                    selectedMessage2={selectedMessage2}
                    setSelectedMessage2={setSelectedMessage2}
                  />
                  <ChecklistForm {...props} />
                </Collapse>
                {errorMessage && (
                  <Box className={classes.mTB10}>
                    <Typography color="error">{errorMessage}</Typography>
                  </Box>
                )}
                <SaveButton
                  handleSubmitWithRedirect={handleSubmitWithRedirect}
                  saving={creating || updating}
                  invalid={invalid}
                  disabled={imageIsSaving}
                />
              </Box>
            </Grid>
          </Grid>
          {isImageModalOpen && (
            <ImageEnlargedModal
              openEdit={isImageModalOpen}
              setOpenEdit={setIsImageModalOpen}
              imageUrl={imageUrl}
            />
          )}
        </Box>
      )}
    />
  );
};

const ImageEnlargedModal = ({ openEdit, setOpenEdit, imageUrl }) => {
  const classes = useStyles();

  // fix this to s3
  return (
    <ModalView openEdit={openEdit} setOpenEdit={setOpenEdit}>
      <Grid container>
        <Grid item xs={12} className={classes.m20}>
          <img
            src={imageUrl}
            alt="Labeled preview"
            className={classes.enlargedImage}
          />
        </Grid>
      </Grid>
    </ModalView>
  );
};

export default CatalogServiceForm;
